import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Tutorial } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

interface HeroCarouselProps {
  tutorials: Tutorial[];
}

export const HeroCarousel: React.FC<HeroCarouselProps> = ({ tutorials }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, skipSnaps: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    
    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [emblaApi, onSelect]);

  const getTagColor = (tag: string) => {
    switch (tag) {
      case '免费': return 'bg-accent-free/10 text-accent-free border-accent-free/20';
      case '付费': return 'bg-accent-paid/10 text-accent-paid border-accent-paid/20';
      case '开源': return 'bg-accent-oss/10 text-accent-oss border-accent-oss/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="relative overflow-hidden group py-4">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {tutorials.map((tutorial) => (
            <div key={tutorial.id} className="flex-[0_0_100%] min-w-0 pl-4 pr-4">
              <div className="relative h-[240px] md:h-[320px] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border border-primary/10 flex flex-col justify-center p-8 md:p-12">
                <div className="absolute top-0 right-0 p-8 md:p-12 opacity-10 pointer-events-none">
                  <ExternalLink className="h-32 w-32 text-primary" />
                </div>
                
                <div className="relative z-10 max-w-2xl">
                  <Badge className={getTagColor(tutorial.tag)}>
                    {tutorial.tag}
                  </Badge>
                  
                  <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-foreground line-clamp-1">
                    {tutorial.name}
                  </h2>
                  
                  <p className="mt-3 text-lg text-muted-foreground line-clamp-2 max-w-lg">
                    {tutorial.description}
                  </p>
                  
                  <div className="mt-6 flex items-center gap-4">
                    <Button 
                      className="px-8 font-bold"
                      onClick={() => navigate(`/juguang-browser?url=${encodeURIComponent(tutorial.link)}`)}
                    >
                      立即前往 <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                    <Badge variant="outline" className="text-sm font-medium py-1 px-3">
                      难度：{tutorial.difficulty}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="absolute left-6 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/50 backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex"
        onClick={scrollPrev}
      >
        <ChevronLeft className="h-6 w-6" />
      </Button>
      
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-6 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/50 backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex"
        onClick={scrollNext}
      >
        <ChevronRight className="h-6 w-6" />
      </Button>

      <div className="flex justify-center gap-2 mt-4">
        {tutorials.map((_, index) => (
          <button
            key={index}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === selectedIndex ? 'w-8 bg-primary' : 'w-2 bg-primary/20 hover:bg-primary/40'
            }`}
            onClick={() => emblaApi?.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};
